import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleDateString('en-MY', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export function formatDateBM(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleDateString('ms-MY', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export function formatDateTimeLocal(value?: string | Date | null): string {
  if (!value) return ''
  const date = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(date.getTime())) return ''

  // Use local time getters to avoid timezone offset issues
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${year}-${month}-${day}T${hours}:${minutes}`
}

export function toIsoFromDateTimeLocal(value?: string | null): string | null {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString()
}

export function parseOptionalNumberInput(value: string): {
  value: number | null
  isValid: boolean
} {
  const trimmed = value.trim()
  if (!trimmed) {
    return { value: null, isValid: true }
  }
  const parsed = Number.parseInt(trimmed, 10)
  if (Number.isNaN(parsed)) {
    return { value: null, isValid: false }
  }
  return { value: parsed, isValid: true }
}

/**
 * Returns a compact human-friendly relative time string for a timestamp.
 * Examples: "Just now", "5m ago", "3h ago", "2d ago", "12 Nov" or "12 Nov 2024"
 */
export function formatRelativeTime(timestamp?: string | Date | null): string {
  if (!timestamp) return 'Unknown'

  const date =
    typeof timestamp === 'string' ? new Date(timestamp) : new Date(timestamp)
  if (Number.isNaN(date.getTime())) return 'Unknown'

  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMinutes < 1) return 'Just now'
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`

  const dateFormatOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  }
  if (date.getFullYear() !== now.getFullYear()) {
    dateFormatOptions.year = 'numeric'
  }

  try {
    return new Intl.DateTimeFormat(undefined, dateFormatOptions).format(date)
  } catch {
    return date.toLocaleDateString()
  }
}

// Returns the normalized WebSocket endpoint URL.
// Accepts NEXT_PUBLIC_WS_URL values that are either a full endpoint
// (e.g. ws://host/ws) or just the base host (e.g. ws://host) and
// guarantees a single `/ws` suffix without duplicating it. When the page
// is served over HTTPS we auto-upgrade to `wss://` to avoid mixed content
// errors in browsers like Firefox.
export function getWebSocketUrl(): string {
  const rawEnvValue = (process.env.NEXT_PUBLIC_WS_URL || '').trim()
  const fallback =
    typeof window !== 'undefined'
      ? `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws`
      : 'ws://localhost:16001/ws'
  const raw = (rawEnvValue || fallback).trim()

  try {
    const url = new URL(raw)
    const isHttpsPage =
      typeof window !== 'undefined' && window.location.protocol === 'https:'
    if (isHttpsPage && url.protocol === 'ws:') {
      url.protocol = 'wss:'
    }

    const cleanedPath = url.pathname.replace(/\/+$/, '')

    if (cleanedPath === '' || cleanedPath === '/') {
      url.pathname = '/ws'
    } else if (!/\/ws$/i.test(cleanedPath)) {
      url.pathname = `${cleanedPath}/ws`
    } else {
      url.pathname = cleanedPath
    }

    // Ensure no trailing slash like '/ws/'
    return url.toString().replace(/\/+$/, '')
  } catch (error) {
    // Fallback for invalid URLs: best-effort normalization
    const normalized = raw.replace(/\/+$/, '')
    return /\/ws$/i.test(normalized) ? normalized : `${normalized}/ws`
  }
}

/**
 * Escapes HTML special characters while preserving unicode characters (emojis, symbols, etc.)
 * This prevents XSS attacks while allowing international characters and symbols
 *
 * @param text - The text to escape
 * @returns The escaped text safe for HTML display
 */
export function escapeHtml(text: string): string {
  if (!text) return ''

  const htmlEscapeMap: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
    '/': '&#x2F;',
  }

  return text.replace(/[&<>"'/]/g, char => htmlEscapeMap[char] || char)
}

/**
 * Returns the agent dashboard URL with the chat tab selected and an optional
 * session preloaded. Encoded to keep the session id safe for use in query params.
 */
export function buildAgentDashboardChatUrl(sessionId?: string | null): string {
  const base = '/agent/dashboard'
  if (!sessionId) {
    return `${base}?tab=chat`
  }

  return `${base}?tab=chat&session=${encodeURIComponent(sessionId)}`
}
